import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import api from "../api";


// ============================================================
// CONSTANTES API
// ============================================================

const PRESTATIONS_API = "/prestations";
const CATEGORIES_API = "/categories-prestations";
const CLIENTS_API = "/clients";
const BLANCHISSERIE_API = "/blanchisserie";


// ============================================================
// CATÉGORIE EXCLUE
// ============================================================

const CATEGORIE_HEBERGEMENT =
  "Hébergement";


// ============================================================
// COMPOSANT
// ============================================================

function BlanchisserieEncaissement() {

  const navigate = useNavigate();


  // ==========================================================
  // CATÉGORIES
  // ==========================================================

  const [
    categories,
    setCategories
  ] = useState([]);


  const [
    loadingCategories,
    setLoadingCategories
  ] = useState(true);


  // ==========================================================
  // PRESTATIONS
  // ==========================================================

  const [
    prestations,
    setPrestations
  ] = useState([]);


  const [
    loadingPrestations,
    setLoadingPrestations
  ] = useState(true);


  // ==========================================================
  // CLIENT
  // ==========================================================

  const [
    clientNom,
    setClientNom
  ] = useState("");


  const [
    clientId,
    setClientId
  ] = useState(null);


  const [
    clients,
    setClients
  ] = useState([]);


  const [
    loadingClients,
    setLoadingClients
  ] = useState(false);


  const [
    showClientSuggestions,
    setShowClientSuggestions
  ] = useState(false);


  const clientSearchTimer =
    useRef(null);


  // ==========================================================
  // PAIEMENT
  // ==========================================================

  const [
    devise,
    setDevise
  ] = useState("USD");


  const [
    modePaiement,
    setModePaiement
  ] = useState("cash");


  const [
    observation,
    setObservation
  ] = useState("");


  // ==========================================================
  // LIGNES
  // ==========================================================

  const [
    lignes,
    setLignes
  ] = useState([
    {
      prestation_id: "",
      designation: "",
      quantite: 1,
      prix_unitaire: "",
    },
  ]);


  // ==========================================================
  // ÉTAT
  // ==========================================================

  const [
    saving,
    setSaving
  ] = useState(false);


  const [
    error,
    setError
  ] = useState("");


  // ==========================================================
  // CATÉGORIES VISIBLES
  // ==========================================================

  const categoriesVisibles =
    useMemo(() => {

      return categories.filter(
        (category) =>
          String(
            category.nom || ""
          )
            .trim()
            .toLowerCase() !==
          CATEGORIE_HEBERGEMENT.toLowerCase()
      );

    }, [categories]);


  // ==========================================================
  // CHARGER CATÉGORIES
  // ==========================================================

  const loadCategories = async () => {

    setLoadingCategories(true);

    try {

      const response =
        await api.get(
          CATEGORIES_API
        );


      const data =
        Array.isArray(
          response.data
        )
          ? response.data
          : Array.isArray(
              response.data?.data
            )
            ? response.data.data
            : Array.isArray(
                response.data?.categories
              )
              ? response.data.categories
              : [];


      setCategories(data);

    } catch (err) {

      console.error(
        "Erreur chargement catégories prestations:",
        err
      );

      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Impossible de charger les catégories de prestations."
      );

    } finally {

      setLoadingCategories(false);

    }
  };


  // ==========================================================
  // CHARGER TOUT LE CATALOGUE
  // ==========================================================

  const loadPrestations = async () => {

    setLoadingPrestations(true);

    try {

      const response =
        await api.get(
          PRESTATIONS_API,
          {
            params: {
              include_inactive: false,
            },
          }
        );


      const data =
        Array.isArray(
          response.data
        )
          ? response.data
          : Array.isArray(
              response.data?.data
            )
            ? response.data.data
            : Array.isArray(
                response.data?.prestations
              )
              ? response.data.prestations
              : [];


      // ------------------------------------------------------
      // Toutes les prestations actives sauf Hébergement
      // ------------------------------------------------------

      const prestationsVisibles =
        data.filter(
          (prestation) => {

            const categorieNom =
              prestation.categorie_nom ||
              prestation.categorie ||
              prestation.categorie_name ||
              prestation.categorie_prestation ||
              "";

            return (
              prestation.actif !== false &&
              String(
                categorieNom
              )
                .trim()
                .toLowerCase() !==
                CATEGORIE_HEBERGEMENT.toLowerCase()
            );
          }
        );


      setPrestations(
        prestationsVisibles
      );

    } catch (err) {

      console.error(
        "Erreur chargement prestations:",
        err
      );

      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Impossible de charger le catalogue des prestations."
      );

      setPrestations([]);

    } finally {

      setLoadingPrestations(false);

    }
  };


  // ==========================================================
  // CHARGEMENT INITIAL
  // ==========================================================

  useEffect(() => {

    loadCategories();
    loadPrestations();

  }, []);


  // ==========================================================
  // RECHERCHE CLIENT
  // ==========================================================

  const rechercherClients = (
    valeur
  ) => {

    const recherche =
      String(
        valeur || ""
      ).trim();


    // --------------------------------------------------------
    // Nouveau texte = plus de client sélectionné
    // --------------------------------------------------------

    setClientId(null);
    setShowClientSuggestions(
      recherche.length >= 2
    );


    if (
      clientSearchTimer.current
    ) {

      clearTimeout(
        clientSearchTimer.current
      );
    }


    if (
      recherche.length < 2
    ) {

      setClients([]);
      setLoadingClients(false);

      return;
    }


    clientSearchTimer.current =
      setTimeout(
        async () => {

          try {

            setLoadingClients(
              true
            );


            const response =
              await api.get(
                CLIENTS_API,
                {
                  params: {
                    search:
                      recherche,
                  },
                }
              );


            const data =
              Array.isArray(
                response.data
              )
                ? response.data
                : Array.isArray(
                    response.data?.data
                  )
                  ? response.data.data
                  : Array.isArray(
                      response.data?.clients
                    )
                    ? response.data.clients
                    : [];


            setClients(data);

          } catch (err) {

            console.error(
              "Erreur recherche client:",
              err
            );

            setClients([]);

          } finally {

            setLoadingClients(
              false
            );

          }

        },
        300
      );
  };


  // ==========================================================
  // NETTOYAGE TIMER
  // ==========================================================

  useEffect(() => {

    return () => {

      if (
        clientSearchTimer.current
      ) {

        clearTimeout(
          clientSearchTimer.current
        );
      }

    };

  }, []);


  // ==========================================================
  // NOM CLIENT
  // ==========================================================

  const getClientNom =
    (client) => {

      return [
        client?.prenom,
        client?.nom,
        client?.post_nom,
      ]
        .filter(
          Boolean
        )
        .join(" ")
        .trim();

    };


  // ==========================================================
  // SÉLECTION CLIENT
  // ==========================================================

  const selectionnerClient = (
    client
  ) => {

    const nom =
      getClientNom(
        client
      );


    setClientId(
      client.id
    );


    setClientNom(
      nom
    );


    setShowClientSuggestions(
      false
    );


    setClients([]);

  };


  // ==========================================================
  // MODIFIER CLIENT
  // ==========================================================

  const modifierClient = (
    valeur
  ) => {

    setClientNom(
      valeur
    );


    rechercherClients(
      valeur
    );

  };


  // ==========================================================
  // CATÉGORIE D'UNE PRESTATION
  // ==========================================================

  const getCategorieNom =
    (prestation) => {

      return (
        prestation.categorie_nom ||
        prestation.categorie ||
        prestation.categorie_name ||
        prestation.categorie_prestation ||
        ""
      );

    };


  // ==========================================================
  // PRESTATIONS GROUPÉES PAR CATÉGORIE
  // ==========================================================

  const prestationsParCategorie =
    useMemo(() => {

      const groupes = {};


      prestations.forEach(
        (prestation) => {

          const categorie =
            getCategorieNom(
              prestation
            ) ||
            "Autres";


          if (
            categorie
              .trim()
              .toLowerCase() ===
            CATEGORIE_HEBERGEMENT.toLowerCase()
          ) {

            return;
          }


          if (
            !groupes[categorie]
          ) {

            groupes[categorie] =
              [];

          }


          groupes[categorie].push(
            prestation
          );

        }
      );


      return groupes;

    }, [prestations]);


  // ==========================================================
  // PRESTATION PAR ID
  // ==========================================================

  const trouverPrestation =
    (id) => {

      return prestations.find(
        (prestation) =>
          Number(
            prestation.id
          ) ===
          Number(id)
      );

    };


  // ==========================================================
  // MODIFIER UNE LIGNE
  // ==========================================================

  const modifierLigne = (
    index,
    champ,
    valeur
  ) => {

    setLignes(
      (precedentes) =>
        precedentes.map(
          (ligne, i) => {

            if (
              i !== index
            ) {

              return ligne;
            }


            return {
              ...ligne,
              [champ]:
                valeur,
            };

          }
        )
    );

  };


  // ==========================================================
  // SÉLECTIONNER UNE PRESTATION
  // ==========================================================

  const selectionnerPrestation = (
    index,
    prestationId
  ) => {

    const prestation =
      trouverPrestation(
        prestationId
      );


    if (!prestation) {

      modifierLigne(
        index,
        "prestation_id",
        ""
      );

      modifierLigne(
        index,
        "designation",
        ""
      );

      modifierLigne(
        index,
        "prix_unitaire",
        ""
      );

      return;
    }


    setLignes(
      (precedentes) =>
        precedentes.map(
          (ligne, i) => {

            if (
              i !== index
            ) {

              return ligne;
            }


            return {

              ...ligne,

              prestation_id:
                prestation.id,

              designation:
                prestation.nom ||
                "",

              prix_unitaire:
                prestation.prix_defaut ??
                "",

            };

          }
        )
    );

  };


  // ==========================================================
  // AJOUTER LIGNE
  // ==========================================================

  const ajouterLigne = () => {

    setLignes(
      (precedentes) => [
        ...precedentes,

        {
          prestation_id: "",
          designation: "",
          quantite: 1,
          prix_unitaire: "",
        },
      ]
    );

  };


  // ==========================================================
  // SUPPRIMER LIGNE
  // ==========================================================

  const supprimerLigne = (
    index
  ) => {

    if (
      lignes.length === 1
    ) {

      return;
    }


    setLignes(
      (precedentes) =>
        precedentes.filter(
          (_, i) =>
            i !== index
        )
    );

  };


  // ==========================================================
  // TOTAL LIGNE
  // ==========================================================

  const calculerTotalLigne =
    (ligne) => {

      const quantite =
        Number(
          ligne.quantite
        ) || 0;


      const prix =
        Number(
          ligne.prix_unitaire
        ) || 0;


      return Number(
        (
          quantite *
          prix
        ).toFixed(2)
      );

    };


  // ==========================================================
  // TOTAL
  // ==========================================================

  const total =
    useMemo(() => {

      return Number(
        lignes
          .reduce(
            (
              somme,
              ligne
            ) =>
              somme +
              calculerTotalLigne(
                ligne
              ),
            0
          )
          .toFixed(2)
      );

    }, [lignes]);


  // ==========================================================
  // FORMAT MONNAIE
  // ==========================================================

  const formatMoney = (
    montant
  ) => {

    return Number(
      montant || 0
    ).toLocaleString(
      "fr-FR",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );

  };


  // ==========================================================
  // ENREGISTREMENT
  // ==========================================================

  const enregistrer =
    async (event) => {

      event.preventDefault();


      setError("");


      // ------------------------------------------------------
      // CLIENT
      // ------------------------------------------------------

      const clientNomFinal =
        clientNom.trim();


      // ------------------------------------------------------
      // LIGNES
      // ------------------------------------------------------

      if (
        lignes.length === 0
      ) {

        setError(
          "Ajoutez au moins une prestation."
        );

        return;
      }


      const lignesPreparees =
        lignes.map(
          (ligne) => {

            const prestation =
              trouverPrestation(
                ligne.prestation_id
              );


            return {

              prestation_id:
                ligne.prestation_id
                  ? Number(
                      ligne.prestation_id
                    )
                  : null,

              designation:
                (
                  ligne.designation ||
                  prestation?.nom ||
                  ""
                ).trim(),

              quantite:
                Number(
                  ligne.quantite
                ),

              prix_unitaire:
                Number(
                  ligne.prix_unitaire
                ),

            };

          }
        );


      // ------------------------------------------------------
      // VALIDATION
      // ------------------------------------------------------

      const ligneInvalide =
        lignesPreparees.some(
          (ligne) =>

            !ligne.prestation_id ||

            !ligne.designation ||

            !Number.isFinite(
              ligne.quantite
            ) ||

            ligne.quantite <= 0 ||

            !Number.isFinite(
              ligne.prix_unitaire
            ) ||

            ligne.prix_unitaire < 0
        );


      if (
        ligneInvalide
      ) {

        setError(
          "Sélectionnez une prestation et vérifiez les quantités et les prix."
        );

        return;
      }


      if (
        total <= 0
      ) {

        setError(
          "Le total doit être supérieur à zéro."
        );

        return;
      }


      try {

        setSaving(true);


        // ----------------------------------------------------
        // PAYLOAD
        // ----------------------------------------------------

        const payload = {

          client_id:
            clientId || null,

          client_nom:
            clientNomFinal ||
            null,

          montant:
            Number(
              total.toFixed(2)
            ),

          devise,

          mode_paiement:
            modePaiement,

          observation:
            observation.trim() ||
            null,

          lignes:
            lignesPreparees,

        };


        console.log(
          "=== ENCAISSEMENT PRESTATION ==="
        );

        console.log(
          payload
        );


        const response =
          await api.post(
            BLANCHISSERIE_API,
            payload
          );


        const id =
          response.data
            ?.encaissement
            ?.id;


        if (id) {

          navigate(
            `/blanchisserie/${id}`
          );

          return;
        }


        navigate(
          "/blanchisserie"
        );

      } catch (err) {

        console.error(
          "Erreur encaissement:",
          err
        );


        setError(
          err.response
            ?.data
            ?.error ||
          err.response
            ?.data
            ?.message ||
          "Impossible d'enregistrer l'encaissement."
        );

      } finally {

        setSaving(false);

      }

    };


  // ==========================================================
  // RENDER
  // ==========================================================

  return (

    <div style={styles.page}>

      {/* ======================================================
          EN-TÊTE
      ====================================================== */}

      <div style={styles.header}>

        <div>

          <h1 style={styles.title}>
            Nouvel encaissement
          </h1>

          <p style={styles.subtitle}>
            Prestations
          </p>

        </div>


        <button
          type="button"
          style={styles.backButton}
          onClick={() =>
            navigate(
              "/blanchisserie"
            )
          }
        >
          ← Retour
        </button>

      </div>


      <form
        onSubmit={
          enregistrer
        }
        style={styles.form}
      >

        {/* ====================================================
            INFORMATIONS CLIENT
        ==================================================== */}

        <div
          style={styles.section}
        >

          <h2
            style={
              styles.sectionTitle
            }
          >
            Client et paiement
          </h2>


          <div
            style={styles.grid}
          >

            {/* CLIENT */}

            <div
              style={{
                ...styles.field,
                position:
                  "relative",
              }}
            >

              <label
                style={styles.label}
              >
                Client
              </label>


              <input
                type="text"
                value={
                  clientNom
                }
                onChange={(e) =>
                  modifierClient(
                    e.target.value
                  )
                }
                onFocus={() => {

                  if (
                    clientNom.trim()
                      .length >= 2
                  ) {

                    setShowClientSuggestions(
                      true
                    );
                  }

                }}
                onBlur={() => {

                  // Petit délai pour permettre
                  // le clic sur une suggestion.

                  setTimeout(
                    () =>
                      setShowClientSuggestions(
                        false
                      ),
                    200
                  );

                }}
                placeholder="Rechercher ou saisir un client"
                autoComplete="off"
                style={
                  styles.input
                }
              />


              {clientId && (

                <div
                  style={
                    styles.clientSelected
                  }
                >
                  ✓ Client enregistré sélectionné
                </div>

              )}


              {!clientId &&
                clientNom.trim() && (

                  <div
                    style={
                      styles.clientExternal
                    }
                  >
                    Client externe / de passage
                  </div>

                )}


              {showClientSuggestions && (
                clientNom.trim()
                  .length >= 2
              ) && (

                <div
                  style={
                    styles.suggestions
                  }
                >

                  {loadingClients ? (

                    <div
                      style={
                        styles.suggestionInfo
                      }
                    >
                      Recherche...
                    </div>

                  ) : clients.length >
                    0 ? (

                    clients.map(
                      (client) => (

                        <button
                          key={
                            client.id
                          }
                          type="button"
                          style={
                            styles.suggestion
                          }
                          onMouseDown={(
                            e
                          ) =>
                            e.preventDefault()
                          }
                          onClick={() =>
                            selectionnerClient(
                              client
                            )
                          }
                        >

                          <div
                            style={
                              styles.suggestionName
                            }
                          >
                            {
                              getClientNom(
                                client
                              )
                            }
                          </div>


                          {client.telephone && (

                            <div
                              style={
                                styles.suggestionMeta
                              }
                            >
                              {
                                client.telephone
                              }
                            </div>

                          )}

                        </button>

                      )
                    )

                  ) : (

                    <div
                      style={
                        styles.suggestionInfo
                      }
                    >
                      Aucun client enregistré correspondant.
                      <br />
                      Le nom saisi sera conservé comme client externe.
                    </div>

                  )}

                </div>

              )}

            </div>


            {/* DEVISE */}

            <div
              style={styles.field}
            >

              <label
                style={styles.label}
              >
                Devise
              </label>


              <select
                value={devise}
                onChange={(e) =>
                  setDevise(
                    e.target.value
                  )
                }
                style={
                  styles.input
                }
              >

                <option value="USD">
                  USD
                </option>

                <option value="CDF">
                  CDF
                </option>

              </select>

            </div>


            {/* MODE PAIEMENT */}

            <div
              style={styles.field}
            >

              <label
                style={styles.label}
              >
                Mode de paiement
              </label>


              <select
                value={
                  modePaiement
                }
                onChange={(e) =>
                  setModePaiement(
                    e.target.value
                  )
                }
                style={
                  styles.input
                }
              >

                <option value="cash">
                  Espèces
                </option>

                <option value="mobile">
                  Mobile Money
                </option>

                <option value="carte">
                  Carte
                </option>

              </select>

            </div>

          </div>

        </div>


        {/* ====================================================
            PRESTATIONS
        ==================================================== */}

        <div
          style={styles.section}
        >

          <div
            style={
              styles.sectionHeader
            }
          >

            <div>

              <h2
                style={
                  styles.sectionTitle
                }
              >
                Prestations
              </h2>

              <p
                style={
                  styles.helper
                }
              >
                Sélectionnez une prestation du catalogue.
                Le prix proposé peut être modifié.
              </p>

            </div>


            <button
              type="button"
              style={
                styles.addButton
              }
              onClick={
                ajouterLigne
              }
            >
              + Ajouter
            </button>

          </div>


          {loadingCategories ||
          loadingPrestations ? (

            <div
              style={
                styles.loading
              }
            >
              Chargement du catalogue...
            </div>

          ) : prestations.length === 0 ? (

            <div
              style={
                styles.empty
              }
            >
              Aucune prestation active disponible.
            </div>

          ) : (

            <div
              style={styles.lines}
            >

              {lignes.map(
                (
                  ligne,
                  index
                ) => (

                  <div
                    key={index}
                    style={
                      styles.line
                    }
                  >

                    {/* PRESTATION */}

                    <div
                      style={{
                        ...styles.field,
                        flex: 3,
                        minWidth:
                          "260px",
                      }}
                    >

                      <label
                        style={
                          styles.label
                        }
                      >
                        Désignation
                      </label>


                      <select
                        value={
                          ligne.prestation_id
                        }
                        onChange={(e) =>
                          selectionnerPrestation(
                            index,
                            e.target.value
                          )
                        }
                        style={
                          styles.input
                        }
                      >

                        <option value="">
                          Sélectionner une prestation
                        </option>


                        {Object.entries(
                          prestationsParCategorie
                        ).map(
                          ([
                            categorie,
                            prestationsCategorie,
                          ]) => (

                            <optgroup
                              key={
                                categorie
                              }
                              label={
                                categorie
                              }
                            >

                              {prestationsCategorie.map(
                                (
                                  prestation
                                ) => (

                                  <option
                                    key={
                                      prestation.id
                                    }
                                    value={
                                      prestation.id
                                    }
                                  >
                                    {
                                      prestation.nom
                                    }
                                    {" — "}
                                    {
                                      formatMoney(
                                        prestation.prix_defaut
                                      )
                                    }{" "}
                                    USD
                                  </option>

                                )
                              )}

                            </optgroup>

                          )
                        )}

                      </select>

                    </div>


                    {/* QUANTITÉ */}

                    <div
                      style={{
                        ...styles.field,
                        flex: 1,
                        minWidth:
                          "110px",
                      }}
                    >

                      <label
                        style={
                          styles.label
                        }
                      >
                        Quantité
                      </label>


                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={
                          ligne.quantite
                        }
                        onChange={(e) =>
                          modifierLigne(
                            index,
                            "quantite",
                            e.target.value
                          )
                        }
                        style={
                          styles.input
                        }
                      />

                    </div>


                    {/* PRIX */}

                    <div
                      style={{
                        ...styles.field,
                        flex: 1,
                        minWidth:
                          "140px",
                      }}
                    >

                      <label
                        style={
                          styles.label
                        }
                      >
                        Prix unitaire
                      </label>


                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={
                          ligne.prix_unitaire
                        }
                        onChange={(e) =>
                          modifierLigne(
                            index,
                            "prix_unitaire",
                            e.target.value
                          )
                        }
                        style={
                          styles.input
                        }
                      />

                    </div>


                    {/* TOTAL */}

                    <div
                      style={{
                        ...styles.field,
                        flex: 1,
                        minWidth:
                          "130px",
                      }}
                    >

                      <label
                        style={
                          styles.label
                        }
                      >
                        Total
                      </label>


                      <div
                        style={
                          styles.lineTotal
                        }
                      >
                        {
                          formatMoney(
                            calculerTotalLigne(
                              ligne
                            )
                          )
                        }{" "}
                        {devise}
                      </div>

                    </div>


                    {/* SUPPRESSION */}

                    <button
                      type="button"
                      onClick={() =>
                        supprimerLigne(
                          index
                        )
                      }
                      disabled={
                        lignes.length ===
                        1
                      }
                      style={
                        lignes.length ===
                        1
                          ? styles.deleteDisabled
                          : styles.deleteButton
                      }
                      title="Supprimer la ligne"
                    >
                      ×
                    </button>

                  </div>

                )
              )}

            </div>

          )}

        </div>


        {/* ====================================================
            OBSERVATION
        ==================================================== */}

        <div
          style={styles.section}
        >

          <div
            style={styles.field}
          >

            <label
              style={styles.label}
            >
              Observation
            </label>


            <textarea
              value={
                observation
              }
              onChange={(e) =>
                setObservation(
                  e.target.value
                )
              }
              rows={3}
              placeholder="Observation éventuelle..."
              style={{
                ...styles.input,
                resize:
                  "vertical",
              }}
            />

          </div>

        </div>


        {/* ====================================================
            TOTAL
        ==================================================== */}

        <div
          style={
            styles.totalBox
          }
        >

          <span>
            TOTAL À ENCAISSER
          </span>


          <strong>
            {
              formatMoney(
                total
              )
            }{" "}
            {devise}
          </strong>

        </div>


        {/* ====================================================
            ERREUR
        ==================================================== */}

        {error && (

          <div
            style={
              styles.error
            }
          >
            {error}
          </div>

        )}


        {/* ====================================================
            ACTIONS
        ==================================================== */}

        <div
          style={
            styles.actions
          }
        >

          <button
            type="button"
            style={
              styles.cancelButton
            }
            onClick={() =>
              navigate(
                "/blanchisserie"
              )
            }
            disabled={saving}
          >
            Annuler
          </button>


          <button
            type="submit"
            disabled={
              saving ||
              loadingPrestations ||
              prestations.length ===
                0
            }
            style={
              styles.saveButton
            }
          >

            {saving
              ? "Enregistrement..."
              : "Enregistrer"}

          </button>

        </div>

      </form>

    </div>

  );

}


// ============================================================
// STYLES
// ============================================================

const styles = {

  page: {
    padding: "24px",
    maxWidth: "1250px",
    margin: "0 auto",
    background:
      "#f5f6f8",
    minHeight:
      "100vh",
  },


  header: {
    display:
      "flex",
    justifyContent:
      "space-between",
    alignItems:
      "center",
    marginBottom:
      "24px",
    gap: "16px",
  },


  title: {
    margin: 0,
    fontSize:
      "28px",
    fontWeight:
      "700",
  },


  subtitle: {
    margin:
      "6px 0 0",
    color:
      "#6b7280",
  },


  backButton: {
    padding:
      "9px 14px",
    borderRadius:
      "8px",
    border:
      "1px solid #d1d5db",
    background:
      "#fff",
    cursor:
      "pointer",
  },


  form: {
    display:
      "flex",
    flexDirection:
      "column",
    gap:
      "18px",
  },


  section: {
    background:
      "#fff",
    padding:
      "20px",
    borderRadius:
      "12px",
    boxShadow:
      "0 2px 8px rgba(0,0,0,0.07)",
  },


  sectionHeader: {
    display:
      "flex",
    justifyContent:
      "space-between",
    alignItems:
      "center",
    gap:
      "16px",
    marginBottom:
      "18px",
  },


  sectionTitle: {
    margin: 0,
    fontSize:
      "18px",
    fontWeight:
      "700",
  },


  helper: {
    margin:
      "5px 0 0",
    color:
      "#6b7280",
    fontSize:
      "13px",
  },


  grid: {
    display:
      "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(240px, 1fr))",
    gap:
      "16px",
  },


  field: {
    display:
      "flex",
    flexDirection:
      "column",
    gap:
      "6px",
  },


  label: {
    fontSize:
      "13px",
    fontWeight:
      "600",
    color:
      "#374151",
  },


  input: {
    width:
      "100%",
    boxSizing:
      "border-box",
    padding:
      "10px 12px",
    border:
      "1px solid #d1d5db",
    borderRadius:
      "8px",
    background:
      "#fff",
    outline:
      "none",
  },


  clientSelected: {
    fontSize:
      "12px",
    color:
      "#15803d",
    marginTop:
      "2px",
  },


  clientExternal: {
    fontSize:
      "12px",
    color:
      "#6b7280",
    marginTop:
      "2px",
  },


  suggestions: {
    position:
      "absolute",
    top:
      "calc(100% + 4px)",
    left:
      0,
    right:
      0,
    background:
      "#fff",
    border:
      "1px solid #d1d5db",
    borderRadius:
      "10px",
    boxShadow:
      "0 8px 25px rgba(0,0,0,0.12)",
    overflow:
      "hidden",
    zIndex:
      100,
    maxHeight:
      "280px",
    overflowY:
      "auto",
  },


  suggestion: {
    display:
      "block",
    width:
      "100%",
    textAlign:
      "left",
    padding:
      "11px 13px",
    border:
      "none",
    borderBottom:
      "1px solid #f0f0f0",
    background:
      "#fff",
    cursor:
      "pointer",
  },


  suggestionName: {
    fontWeight:
      "600",
    color:
      "#111827",
  },


  suggestionMeta: {
    marginTop:
      "3px",
    fontSize:
      "12px",
    color:
      "#6b7280",
  },


  suggestionInfo: {
    padding:
      "14px",
    color:
      "#6b7280",
    fontSize:
      "13px",
    lineHeight:
      "1.5",
  },


  loading: {
    padding:
      "30px",
    textAlign:
      "center",
    color:
      "#6b7280",
  },


  empty: {
    padding:
      "30px",
    textAlign:
      "center",
    color:
      "#6b7280",
    background:
      "#f9fafb",
    borderRadius:
      "8px",
  },


  lines: {
    display:
      "flex",
    flexDirection:
      "column",
    gap:
      "12px",
  },


  line: {
    display:
      "flex",
    alignItems:
      "flex-end",
    gap:
      "10px",
    flexWrap:
      "wrap",
    padding:
      "12px",
    border:
      "1px solid #e5e7eb",
    borderRadius:
      "10px",
    background:
      "#fafafa",
  },


  lineTotal: {
    minHeight:
      "40px",
    display:
      "flex",
    alignItems:
      "center",
    fontWeight:
      "700",
    color:
      "#111827",
  },


  addButton: {
    padding:
      "9px 15px",
    border:
      "none",
    borderRadius:
      "8px",
    background:
      "#1d4ed8",
    color:
      "#fff",
    cursor:
      "pointer",
    fontWeight:
      "600",
    whiteSpace:
      "nowrap",
  },


  deleteButton: {
    width:
      "40px",
    height:
      "40px",
    border:
      "none",
    borderRadius:
      "8px",
    background:
      "#fee2e2",
    color:
      "#b91c1c",
    fontSize:
      "22px",
    cursor:
      "pointer",
  },


  deleteDisabled: {
    width:
      "40px",
    height:
      "40px",
    border:
      "none",
    borderRadius:
      "8px",
    background:
      "#f3f4f6",
    color:
      "#9ca3af",
    fontSize:
      "22px",
  },


  totalBox: {
    display:
      "flex",
    justifyContent:
      "space-between",
    alignItems:
      "center",
    padding:
      "20px",
    borderRadius:
      "12px",
    background:
      "#f8fafc",
    fontSize:
      "18px",
  },


  error: {
    padding:
      "12px 16px",
    borderRadius:
      "8px",
    background:
      "#fee2e2",
    color:
      "#991b1b",
  },


  actions: {
    display:
      "flex",
    justifyContent:
      "flex-end",
    gap:
      "10px",
  },


  cancelButton: {
    padding:
      "11px 20px",
    borderRadius:
      "8px",
    border:
      "1px solid #d1d5db",
    background:
      "#fff",
    cursor:
      "pointer",
  },


  saveButton: {
    padding:
      "11px 24px",
    borderRadius:
      "8px",
    border:
      "none",
    background:
      "#1d4ed8",
    color:
      "#fff",
    cursor:
      "pointer",
    fontWeight:
      "700",
  },

};


export default BlanchisserieEncaissement;